import { useMutation, useQuery } from "@tanstack/react-query";
import { withNamespaces } from "react-i18next";
import { toast } from "react-toastify";
import { Card, CardBody, Col, Row, Spinner, Alert, Input, Button, UncontrolledTooltip, UncontrolledPopover, PopoverHeader, PopoverBody } from "reactstrap";
import BootstrapTable from "react-bootstrap-table-next";
import overlayFactory from 'react-bootstrap-table2-overlay';
import paginationFactory, { 
    PaginationProvider, 
    PaginationListStandalone,
    SizePerPageDropdownStandalone,
    PaginationTotalStandalone
} from 'react-bootstrap-table2-paginator';
import { connect } from "react-redux";
import { EMPTY_LIST } from "src/common/constants";
import { useCallback, useEffect, useMemo, useState } from "react";
import FilteringComponent from "./components/FilteringComponent";
import CommonService from "src/modules/3rd-party-management/apis/common/CommonService";
import { Link } from "react-router-dom";
import EudrDeclarationService from "src/modules/3rd-party-management/apis/thirdparty/EudrDeclarationService";
import GetGeoLocationDataModal from "./components/GetGeoLocationDataModal";
import { 
    API_BASE_URL, 
    DDSTypes, 
    ROUTE_PATH_THIRDPARTY_EUDR_DUE_DILIGENCE_ADD_GEO, 
    ROUTE_PATH_THIRDPARTY_EUDR_DUE_DILIGENCE_DECLARE 
} from "src/modules/3rd-party-management/constants";
import DateUtils from "src/services/utils/DateUtils";
import {
    INTERNATIONAL_DATE_FORMAT
} from 'src/common/constants';
import ConfirmForm from "./components/ConfirmForm";
import { useHistory } from "react-router-dom";
import { 
    SimpleQuestionnaire,
    helpers
} from "@smartintegrity/questionnaire";
import axios from "axios";
import { RiInformationLine } from "react-icons/ri";
import InvalidSelectionModal from "./components/InvalidSelectionModal";
import TableColumnCustomizer from "src/modules/3rd-party-management/components/TableColumnCustomizer";
import useTableColumnCustomizer from "src/modules/3rd-party-management/components/TableColumnCustomizer/CustomizerHook";
import QuestionnaireService from 'src/modules/3rd-party-management/apis/common/QuestionnaireService';

const WoodQuestionInfoTooltip = ({ t, q }) => {
    return (
        <span>
            <i
                id={`question-${q.front_id}-tooltip`}
                className="ri-information-line"
            />
            <UncontrolledPopover
                trigger="hover"
                target={`question-${q.front_id}-tooltip`}
            >
                <PopoverHeader tag="h5">
                    {t('The EUTR applies to a broad range of products classified under various Combined Nomenclature (CN) codes, including:')}
                </PopoverHeader>
                <PopoverBody>
                    <ul className="m-0">
                        <li>
                            <strong>{t('Raw Timber')}:</strong>{' '}
                            {t('Logs, roughly squared wood.')}
                        </li>
                        <li>
                            <strong>{t('Sawn Timber')}:</strong>{' '}
                            {t('Lumber, wood exceeding 6 mm in thickness.')}
                        </li>
                        <li>
                            <strong>{t('Wood Panels')}:</strong>{' '}
                            {t('Plywood, particleboard, OSB, and fiberboard.')}
                        </li>
                        <li>
                            <strong>{t('Pulp and Paper')}:</strong>{' '}
                            {t('Most paper and wood pulp products (excluding printed matter like books).')}
                        </li>
                        <li>
                            <strong>{t('Finished Wood Products')}:</strong>{' '}
                            {t("Wooden furniture (office, kitchen, bedroom, and 'other' wooden furniture).")}
                        </li>
                        <li>
                            <strong>{t("Builder's joinery")}:</strong>{' '}
                            {t("Doors, windows, assembled flooring.")}
                        </li>
                    </ul>
                </PopoverBody>
            </UncontrolledPopover>
        </span>
    )
}

const eudrDefaultCols = [
    // 'id',
    'name',
    'productName',
    'lotNumber',
    'importDate',   
    'metricTonnes',
    'originCountry',
    'ddsReferenceNumber',
    'ddsVerificationNumber',
    'statementValidation',
    'hsCode',
    'knCode',
    'geoStatus',
    // 'containerNumber',
    // 'shipmentNumber',
    'actions'
];

const ListProducts = ({
    t,
    token,
    Organization,

    openReportRequest
}) => {
    const [reportCase, setReportCase] = useState({
        caseId: null,
        refetch: false
    });
    const [ questions, setQuestions ] = useState([]);
    const [ prevAnswers, setPrevAnswers ] = useState([]);

    const [productToAddGeoData, setProductToAddGeoData] = useState(null);
    const [filters, setFilters] = useState({
        pageIndex: 1,
        pageSize: 25,
        sortField: null,
        sortOrder: null
    });
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [showInvalidSelectionModal, setShowInvalidSelectionModal] = useState(false);
    const [showConfirmForm, setShowConfirmForm] = useState(false);
    const [showQuestionnaireQuestions, setShowQuestionniareQuestions] = useState(false);
    const [questionnaireSubmitInProcess, setQuestionnaireSubmitInProcess] = useState(false);

    const history = useHistory();

    const dateUtils = new DateUtils();

    const {
        data: prevAnswersData,
        refetch: refetchPrevAnswers
    } = useQuery({
		queryKey: [
            '3rd-party-fetch-questionnaire-prev-answers-query',
            'eudr',
            Organization.id,
            openReportRequest.request.id
        ],
		queryFn: async () => {
			const service = QuestionnaireService.getInstance();
	
			return await service.fetchPrevFilledAnswers(
                'eudr', 
                Organization.id, 
                {}
            );
		},
		cacheTime: 0,
		refetchOnWindowFocus: false,
		retry: 0,
		onError: ({
			response
		}) => {
			if((response.data.error).toLowerCase() === 'there is not any report for this questionnaire'){
				axios.post(`${API_BASE_URL}/report.create`, {
					"questionnaireType"		:	'eudr',
					"questionAnswers"	    :	[],
                    "reportRequest"         :   openReportRequest.request.id
				}, {
					headers: {
						Authorization: `Bearer ${token}`,
					}
				})
				.then(({ data }) => {
					setReportCase({
                        caseId: data?.data?.report_case_id,
                        refetch: true
                    });
				})
				.catch(() => {
                    history.push('/third-party/3rd-party/home');
				});
			}
		},
        onSuccess: ({ reportCaseId }) => {
            setReportCase({
                caseId: reportCaseId,
                refetch: false
            })
        }
	});

    const {
        isFetching: productsAreFetching,
        isFetched: productsFetched,
        data: products,
        refetch: refetchProducts
    } = useQuery({
        queryKey: [
            '3rd-party-management-fetch-supplier-eudr-products',
            Organization.id,
            filters
        ],
        queryFn: async () => {
            const service = EudrDeclarationService.getInstance();

            return await service.list(Organization.id, filters);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('An error occurred while fetching products list.'), {
                type: 'error'
            });
        }
    });

    const handleFetchCountriesQuery = useQuery({
        queryKey: ['3rd-party-management-fetch-countries-list'],
        queryFn: async () => {
            const service = CommonService.getInstance();

            return await service.fetchCountries();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('An error occurred while fetching locations.'), {
                type: 'error'
            });
        }
    });

    const {
        mutate: submitDeclaration,
        isLoading: declarationIsSubmiting
    } = useMutation({
        mutationFn: async (payload) => {
            const service = EudrDeclarationService.getInstance();
            return await service.finalize(reportCase.caseId, payload);
        },
        onSuccess: () => {
            toast(t('Request Submitted'), {
                type: 'success',
            });

            setShowConfirmForm(false);

            history.push('/third-party/3rd-party/reports');
        },
        onError: () => {
            toast(t("An error occurred while submitting the request."), {
                type: "error"
            });
        }
    });

    const handleCheckboxChange = useCallback((product) => {
        setSelectedProducts((currentState) => {
            if (currentState.findIndex((item) => {
                return item.id === product.id
            }) > -1) {
                return currentState.filter((item) => item.id !== product.id);
            } else {
                return [...currentState, {...product}];
            }
        });
    }, []);

    const eudrAllColumns = useMemo(() => {
        return [
            {
                dataField: "checkbox",
                text: '',
                sort: false,
                key: 0,
                locked: true,
                formatter: (cellContent, row) => {
                    return (
                        <Input
                            type="checkbox"
                            checked={selectedProducts.findIndex((product) => product.id === row.id) > -1}
                            onChange={() => handleCheckboxChange(row)}
                        />
                    );
                }
            },
            {
                dataField: "id",
                text: t("ID"),
                sort: true,
                key: 1,
                style: {
                    width: '80px'
                }
            },
            {
                dataField: "name",
                text: t("NACE Code"),
                sort: true,
                key: 2,
                style: {
                    width: '200px'
                },
                formatter: (cellContent) => {
                    return `${cellContent.class} - ${t(cellContent.name)}`
                }
            },
            {
                dataField: "productName",
                text: t("Product Name"),
                sort: true,
                key: 3,
                formatter: (_cell, { product_name }) => {
                    return product_name
                }
            },
            {
                dataField: "lotNumber",
                text: t("Lot Number"),
                sort: true,
                key: 4,
                formatter: (_cell, { lot_number }) => {
                    return lot_number
                }
            },
            {
                dataField: "importDate",
                text: t("Date of Import"),
                sort: true,
                key: 5,
                formatter: (_cellContent, { import_date }) => {
                    return (
                        dateUtils.convertDateToDate(import_date, INTERNATIONAL_DATE_FORMAT)
                    )
                }
            },
            {
                dataField: "importFromCountry",
                text: t("Import Country"),
                sort: true,
                key: 6,
                formatter: (_cellContent, { import_from_country }) => {
                    return t(import_from_country?.name)
                }
            },
            {
                dataField: "exportDate",
                text: t("Date of Export"),
                sort: true,
                key: 7,
                formatter: (_cellContent, { export_date }) => {
                    return (
                        dateUtils.convertDateToDate(export_date, INTERNATIONAL_DATE_FORMAT)
                    )
                }
            },
            {
                dataField: "exportToCountry",
                text: t("Export Country"),
                sort: true,
                key: 8,
                formatter: (_cellContent, { export_to_country }) => {
                    return t(export_to_country?.name)
                }
            },
            {
                dataField: "metricTonnes",
                text: t("Quantity (Metric Tonnes)"),
                sort: true,
                key: 9,
                formatter: (_cellContent, { metric_tonnes }) => {
                    return metric_tonnes
                }
            },
            {
                dataField: "originCountry",
                text: t("Country of Origin"),
                sort: true,
                key: 10,
                formatter: (_cellContent, { origin_country }) => {
                    return t(origin_country?.name)
                }
            },
            {
                dataField: "ddsDeclarationType",
                text: t("DDS Declaration Type"),
                sort: true,
                key: 11,
                formatter: (_cellContent, { dds_declaration_type }) => {
                    return t(DDSTypes[dds_declaration_type]?.title)
                }
            },
            {
                dataField: "ddsReferenceNumber",
                text: t("DDS Reference Number"),
                sort: true,
                key: 12,
                formatter: (_cellContent, { dds_reference_number }) => {
                    return dds_reference_number
                }
            },
            {
                dataField: "ddsVerificationNumber",
                text: t("DDS Verification Number"),
                sort: true,
                key: 13,
                formatter: (_cellContent, { dds_verification_number }) => {
                    return dds_verification_number
                }
            },
            {
                dataField: "statementValidation",
                text: t("DDS Statement Status"),
                sort: true,
                key: 14,
                formatter: (cellContent, { dds_declaration_type }) => {
                    if (dds_declaration_type === 'provide_reference_numbers') {
                        if (cellContent === false) {
                            return (
                                <span>
                                    <i className="ri-close-line me-1 text-danger align-middle font-size-18" />
                                    {t('Invalid')}
                                </span>
                            )
                        }
                        else if (cellContent === true) {
                            return (
                                <span>
                                    <i className="ri-checkbox-fill me-1 text-success align-middle font-size-18" />
                                    {t('Valid')}
                                </span>
                            )
                        }
                        else {
                            return (
                                <span>
                                    {t('Validating')}...
                                </span>
                            )
                        }
                    }

                    return '-'
                }
            },
            {
                dataField: "geo_status",
                text: t("Geo Status"),
                sort: false,
                key: 15,
                formatter: (_cell, { geolocation_reference }) => {
                    if (geolocation_reference?.length > 0) {
                        return (
                            <span>
                                <i className="ri ri-check-line me-1 text-success font-size-20 align-middle" />
                                {t('Provided')}
                            </span>
                        )
                    }

                    return (
                        <span>
                            <i className="ri ri-close-line me-1 text-danger font-size-20 align-middle" />
                            {t('Not Provided')}
                        </span>
                    )
                }
            },
            {
                dataField: "hsCode",
                text: t("HS Code (6-digit)"),
                sort: true,
                key: 16,
                formatter: (_cellContent, { hs_code }) => {
                    return hs_code
                }
            },
            {
                dataField: "knCode",
                text: t("KN Code (8-digit)"),
                sort: true,
                key: 17,
                formatter: (_cellContent, { kn_code }) => {
                    return kn_code
                }
            },
            {
                dataField: "taricCode",
                text: t("TARIC Code (10-digit)"),
                sort: true,
                key: 18,
                formatter: (_cellContent, { taric_code }) => {
                    return taric_code
                }
            },
            {
                dataField: "containerNumber",
                text: t("Container Number"),
                sort: true,
                key: 19,
                formatter: (_cell, { container_number }) => {
                    return container_number
                }
            },
            {
                dataField: "shipmentNumber",
                text: t("Shipment Number"),
                sort: true,
                key: 20,
                formatter: (_cell, { shipment_number }) => {
                    return shipment_number
                }
            },
            {
                dataField: "description",
                text: t('Description'),
                sort: false,
                key: 21
            },
            {
                dataField: "actions",
                text: t("Action"),
                sort: false,
                key: 22,
                style: {
                    width: '120px'
                },
                formatter: (_cellContent, row) => {
                    return (
                        <div className="actions">
                            <Button
                                size="md"
                                color="primary"
                                aria-label={`${t('Add geolocation for product')} ${row.name.name}`}
                                onClick={() => {
                                    if ((row.commodity)?.toLowerCase() === 'cattle') {
                                        history.push(
                                            `${ROUTE_PATH_THIRDPARTY_EUDR_DUE_DILIGENCE_ADD_GEO}?product=${row.id}`
                                        )
                                    }
                                    else {
                                        setProductToAddGeoData(row);
                                    }
                                }}
                                id={`geoBtn-${row.id}`}
                            >
                                <i className="ri-map-pin-line me-1" />
                                {t('Add Geolocation')}
                            </Button>
                            <UncontrolledTooltip
                                placement="left"
                                target={`geoBtn-${row.id}`}
                            >
                                {t('Draw the origin plot on a map to provide required EUDR geolocation data.')}
                            </UncontrolledTooltip>
                        </div>
                    )
                }
            }
        ]
    }, [
        t,
        selectedProducts,
        handleCheckboxChange
    ]);

    const {
        selectedColumns,
        onColumnsChange
    } = useTableColumnCustomizer({
        defaultSelectedCols: eudrDefaultCols,
        uniqueIdentifier: 'tpdd-supplier-ddr-products-cols'
    });

    const visibleColumns = useMemo(() => {
        return eudrAllColumns.filter((col) => selectedColumns.includes(col.dataField) || col.locked)
    }, [
        eudrAllColumns,
        selectedColumns
    ]);

    const settings = useMemo(() => {
        let submitButtonSettings = {
            enable: !questionnaireSubmitInProcess,
            title: questionnaireSubmitInProcess ? (
                <>
                    <Spinner color="white" size="sm" className="me-2" />
                    {t('Please wait')}...
                </>
            ) :  t('Next')
        }

        return {
            panelSettings: {
                showNumber: true,
                showProgressBar: false,
                prefix: ''
            },
            buttonsSettings: {
                submit: {
                    display: true,
                    ...submitButtonSettings
                },
                back: {
                    display: true,
                    enable: true,
                    onClick: () => {
                        setShowQuestionniareQuestions(false);
                    }
                }
            },
            fileUploaderOptions: {
                deleteRequestOptions: {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    payload: {},
                    requestUrl: ({
                        questionId,
                        file
                    }) => {
                        return `${API_BASE_URL}/attachment/${file.id}/delete`
                    }
                },
                uploadRequestOptions: {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    payload: {},
                    requestUrl: ({
                        questionId
                    }) => {
                        return `${API_BASE_URL}/report.uploadAttachment`
                    }
                }
            },
            questions: {
                showNumber: true
            }
        }
    }, [
        token,
        t,
        questionnaireSubmitInProcess
    ]);

    const NoDataIndication = () => {
        if (productsFetched && !products?.length) {
            return (
                <div className="alert m-0" role="alert">
                    <p className="text-center mb-0">
                        {t(EMPTY_LIST)}
                    </p>
                </div>
            )
        }

        return <></>
    }

    const handleTableFilterChange = useCallback((newFilters) => {
        setFilters((currentState) => {
            return {
                ...currentState,
                ...newFilters,
                pageIndex: 1
            }
        })
    }, []);

    const handleSubmitQuestionnaire = useCallback((payload) => {
        setQuestionnaireSubmitInProcess(true);

        axios.post(`${API_BASE_URL}/report/${ reportCase.caseId }/edit`, {
            questionAnswers: payload,
            questionnaireType: 'eudr'
        }, {
			headers: {
				Authorization: `Bearer ${token}`
			}
		}).then(() => {
            setQuestionnaireSubmitInProcess(false);
            setShowQuestionniareQuestions(false);
            setShowConfirmForm(true);
        });
    }, [
        setShowConfirmForm,
        setShowQuestionniareQuestions,
        setQuestionnaireSubmitInProcess,
        reportCase.caseId,
        token
    ]);

    const handleTableChange = (type, options) => {
        const {
            page,
            sizePerPage,

            sortField,
            sortOrder
        } = options;

        switch(type){
            case 'pagination':
                    setFilters({
                        ...filters,
                        pageIndex   :   page,
                        pageSize    :   sizePerPage
                    });
                break;
            case 'sort':
                    setFilters({
                        ...filters,
                        sortField   :   sortField,
                        sortOrder   :   sortOrder.toUpperCase()
                    })
                break;
            default:
                return false;
        }
    }

    const handleStartReportButtonClick = () => {
        const uniqueOrigins = new Set(selectedProducts.map(p => p.origin_country?.id));
        const uniqueImportDates = new Set(selectedProducts.map(
            p => (p.import_date || '')?.split(' ')[0])
        );

        if (uniqueOrigins.size > 1 || uniqueImportDates.size > 1) {
            return setShowInvalidSelectionModal(true);
        }

        history.push(`${ROUTE_PATH_THIRDPARTY_EUDR_DUE_DILIGENCE_DECLARE}?products=${selectedProducts.map((p) => p.id).join(',')}`)
    }

    useEffect(() => {
        if(reportCase.refetch) {
            refetchPrevAnswers()
        }
    }, [
        reportCase.refetch
    ]);

    useEffect(() => {
        if(prevAnswersData?.questions && productsFetched){
            let gotQuestions = prevAnswersData.questions.map((q) => {
                if (q.front_id === '15u1pAMkF000iIhg7aHlKY400') {
                    return {
                        ...q,
                        info: ({ t }) => <WoodQuestionInfoTooltip q={q} t={t} />
                    }
                }

                return q
            });

            if (!products?.hasWoodProduct) {
                gotQuestions = gotQuestions.filter((q) => q.front_id !== '15u1pAMkF000iIhg7aHlKY400')
            }

            setPrevAnswers(helpers.makePreviousAnswersReadyForQuestionnaire(
                gotQuestions,
                gotQuestions
            ));

            setTimeout(() => {
                setQuestions(helpers.makeQuestionsReadyForQuestionnaire(
                    gotQuestions
                ));
            }, 1000);
        }
    }, [ 
        prevAnswersData,
        products?.hasWoodProduct,
        productsFetched
    ]);

    if(showQuestionnaireQuestions) {
        return (
            <Row>
                <Col sm='12'>
                    <Alert 
                        color="info"
                        className="d-flex align-items-center"
                    >
                        <div style={{width: '60px'}}>
                            <RiInformationLine size="24" />
                        </div>
                        <p className="mb-0">
                            {t('For the plots you have selected on the map, ensure you have uploaded documentation confirming that each plot complies with local laws, including land rights, labor rights, and protections for Indigenous Peoples. Where applicable, include proof of consultation and signed agreements with Indigenous Peoples or local communities (FPIC).')}
                        </p>
                    </Alert>
                </Col>

                <Col sm='12'>
                    <Card>
                        <CardBody>
                            <SimpleQuestionnaire
                                questionnaireUniqueId='eudr'
                                className='tpdd'
                                questions={questions || []}
                                defaultValues={prevAnswers}
                                settings={settings}
                                onSubmit={handleSubmitQuestionnaire}
                            />
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        )
    }

    if(showConfirmForm) {
        return (
            <ConfirmForm 
                onConfirm={() => submitDeclaration()}
                isLoading={declarationIsSubmiting}
            />
        )
    }

    return (
        <Row>
            {products?.isAllProductsProcessed && prevAnswersData?.reportRequestStatus === 'waiting' && (
                <Col sm='12'>
                    <Alert color="success" className="d-flex justify-content-between align-items-center alert alert-success fade show flex-column flex-md-row">
                        <span>
                            {t('All selected products have been declared. Do you want to complete this request now?')}
                        </span>
                        <div className="mt-2">
                            <Button 
                                color="primary" 
                                size="sm" 
                                className="me-2"
                                onClick={() => setShowQuestionniareQuestions(true)}
                            >
                                {t('Complete Request')}
                            </Button>
                        </div>
                    </Alert>
                </Col>
            )}

            <Col sm='12'>
                <Alert color="info" aria-describedby="productsHelpSr" className="d-flex align-items-center">
                    <RiInformationLine size="24" />
                    <p id="productsHelp" className="mb-0">
                        {t('This table lists all your products. Use the search or filters to find specific items, then select one or more products to start a due diligence report.')}
                    </p>
                </Alert>

                <p id="productsHelpSr" className="visually-hidden">
                    {t('Use the checkboxes in the first column to select one or more products. After selecting, press the button labeled Start Report to continue the workflow.')}
                </p>
            </Col>

            <Col sm='12'>
                <div
                    className='d-flex justify-content-between align-items-center flex-row-reverse flex-wrap flex-sm-wrap mb-3 mt-3'
                >
                    <div className="d-flex justify-content-end align-items-center">
                        <FilteringComponent
                            onChange={handleTableFilterChange}
                            countries={handleFetchCountriesQuery}
                        />

                        <Button
                            className="d-none d-lg-block ms-2"
                            color="primary"
                            type="button"
                            disabled={!selectedProducts.length}
                            onClick={handleStartReportButtonClick}
                        >
                            {t('Start Report')}
                        </Button>

                        {/* <Link
                            to={`/third-party/3rd-party/eudr-due-diligence/declare?products=${selectedProducts.join(',')}`}
                            className='btn btn-primary d-none d-lg-block ms-2'
                            disabled={!selectedProducts.length}
                        >
                            {t('Start Report')}
                        </Link> */}
                    </div>

                    <div className="d-flex justify-content-between mt-3 w-100">
                        <TableColumnCustomizer 
                            allColumns={eudrAllColumns}
                            visibleColumns={selectedColumns}
                            onChange={onColumnsChange}
                        />

                        <Link
                            to={`${ROUTE_PATH_THIRDPARTY_EUDR_DUE_DILIGENCE_DECLARE}?products=${selectedProducts.join(',')}`}
                            className='btn btn-primary d-lg-none'
                            disabled={!selectedProducts.length}
                        >
                            {t('Start Report')}
                        </Link>
                    </div>
                </div>
            </Col>

            <Col sm='12'>
                <Card>
                    <CardBody>
                        <PaginationProvider
                            pagination={
                                paginationFactory({
                                    custom: true,
                                    page: filters.pageIndex,
                                    sizePerPage: filters.pageSize,
                                    totalSize: products?.itemsCount || 0,
                                    withFirstAndLast: true,
                                    alwaysShowAllBtns: true,
                                    prePageText: <span><i className="ri-arrow-left-s-line"></i> {t('Back')}</span>,
                                    nextPageText: <span>{t('Next')} <i className="ri-arrow-right-s-line"></i></span>,
                                    prePageTitle: t('Pre page'),
                                    firstPageTitle: t('First page'),
                                    lastPageTitle: t('Last page'),
                                    showTotal: false,
                                    paginationTotalRenderer: (from, to, size) => {
                                        return (
                                            <span className="react-bootstrap-table-pagination-total">
                                                {`${t('Total Items')} ${size}`}
                                            </span>
                                        )
                                    },
                                    hideSizePerPage: false,
                                    sizePerPageList: [
                                        {
                                            text: '25',
                                            value: 25
                                        },
                                        {
                                            text: '50',
                                            value: 50
                                        }
                                    ]
                                })
                            }>
                            {
                                ({
                                    paginationProps,
                                    paginationTableProps
                                }) => (
                                    <>
                                        <Row>
                                            <Col sm="12">
                                                <BootstrapTable
                                                    remote={{
                                                        pagination: true,
                                                        filter: false,
                                                        sort: true,
                                                        cellEdit: false,
                                                        search: false
                                                    }}
                                                    loading={productsAreFetching}
                                                    overlay={overlayFactory({ spinner: <Spinner animation="border" variant="primary" size="md" />, text: `${t("Loading")}...` })}
                                                    onTableChange={handleTableChange}
                                                    defaultSorted={[]}
                                                    keyField={"secondaryId"}
                                                    responsive={true}
                                                    bordered={false}
                                                    data={products?.products || []}
                                                    striped={true}
                                                    columns={visibleColumns}
                                                    wrapperClasses="table-responsive"
                                                    classes={"table tpdd-table"}
                                                    headerWrapperClasses={"thead-light"}
                                                    noDataIndication={() => <NoDataIndication />}
                                                    {...paginationTableProps}
                                                />
                                            </Col>
                                        </Row>

                                        <Row>
                                            <Col sm="12">
                                                <div className="tpdd-pagination-style-1">
                                                    <PaginationTotalStandalone
                                                        {...paginationProps}
                                                    />

                                                    <div>
                                                        <PaginationListStandalone
                                                            {...paginationProps}
                                                        />

                                                        <SizePerPageDropdownStandalone
                                                            {...paginationProps}
                                                        />
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    </>
                                )
                            }

                        </PaginationProvider>
                    </CardBody>
                </Card>
                
                <GetGeoLocationDataModal 
                    show={!!productToAddGeoData}
                    toggle={() => setProductToAddGeoData(null)}
                    product={productToAddGeoData}
                    onSuccess={refetchProducts}
                />
            </Col>

            <InvalidSelectionModal 
                isOpen={showInvalidSelectionModal}
                toggle={() => setShowInvalidSelectionModal(false)}
            />
        </Row>
    )
};

const mapStatetoProps = (state) => {
    const { Organization } = state;
    const { token } = state.Login;

    return {
        Organization,
        token
    };
};

export default withNamespaces()(
    connect(mapStatetoProps)(
        ListProducts
    )
);